import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AlertCircle } from 'tabler-icons-react';
import { Divider, Progress, TextInput } from '@mantine/core';
import AppStack from '../../common/AppStack';
import ConfirmModal from '../../common/ConfirmModal';
import AppText from '../../common/AppText';
import { dataUrlToFile, uploadFileToStorage } from '../../../helpers/awsHelper';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import { singularPluralFormat } from '../../../helpers/format';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppImage from '../../common/AppImage';
import AppCheckbox from '../../common/AppCheckbox';
import { triggerNotification } from '../../../helpers/notification';
import { getDefaultPrintTemplates } from './helpers/fabricCanvasHelper';

const PrintDesignEditorSaveChangesModal = ({
  fkEcomStore,
  fkEcomVendor,
  ecomExternalProducts,
  formState,
  setFormState,
  printTemplateState,
  setPrintTemplateState,
  canvasController,
  onSaveChanges,
  onSaveChangesComplete,
  onCreateDesigns,
  options
}) => {
  const hasBeenCancelled = useRef(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [saveState, setSaveState] = useState({
    addToDesigns: true,
    appendDesignName: true,
    designNameSeperator: '-',
    isPrivate: true,
    allPrintDesigns: [],
    designs: [],
    pendingPrintDesigns: [],
    completedPrintDesigns: []
  });
  const {
    getCanvasSaveState,
    createAndPopulateHiddenCanvasState
  } = canvasController;

  const {
    designsWithChanges,
    designsWithoutChanges
  } = formState.productDesigns.reduce(
    (acc, template) => {
      if (
        template.hasChanges ||
        (printTemplateState.hasChanges && template.id === printTemplateState.id)
      ) {
        acc.designsWithChanges.push(template);
      }
      else {
        acc.designsWithoutChanges.push(template);
      }
      return acc;
    },
    { designsWithChanges: [], designsWithoutChanges: [] }
  );

  const blankPrintDesigns = formState.productDesigns.filter(
    (t) => t.id !== printTemplateState.id || !printTemplateState.hasChanges
  );
  const newProducts = designsWithChanges.filter((d) => !d.pkProduct);
  const exampleNewProduct = ecomExternalProducts.find(
    (p) => p.id === newProducts[0]?.productId
  );

  const newProductDesigns = designsWithChanges.reduce((r, c) => {
    const pkMedia = c.pkEcomVendorMedia || c.pkEcomStoreMedia;
    const design = r.find((i) => i.pkMedia === pkMedia);
    if (pkMedia && !design) {
      const existingDesign = options.designs?.find(
        (d) => d.pkMedia === pkMedia
      );
      r.push({
        pkDesign: existingDesign?.pkDesign || null,
        name: existingDesign?.name || '',
        preview: c.preview,
        pkMedia
      });
    }
    return r;
  }, []);

  useEffect(() => {
    if (formState.showConfirmModal) {
      setSaveState((prev) => ({
        ...prev,
        allPrintDesigns: [],
        pendingPrintDesigns: [],
        completedPrintDesigns: [],
        designs: [
          ...prev.designs.map((d) => ({
            ...d,
            deleted:
              newProductDesigns.findIndex((i) => i.pkMedia === d.pkMedia) === -1
          })),
          ...newProductDesigns
            .filter((d1) =>
              prev.designs.every((d2) => d2.pkMedia !== d1.pkMedia)
            )
            .map((d) => ({ ...d, deleted: false }))
        ]
      }));
      hasBeenCancelled.current = false;
    }
  }, [formState.showConfirmModal]);

  const savePrintDesignsRecursively = (data, uploadedDesigns = []) => {
    if (hasBeenCancelled.current) {
      setFormState((prev) => ({ ...prev, loading: false }));
    }
    else if (data.length > 0) {
      const onComplete = (isSuccess, d) => {
        setSaveState((prevState) => ({
          ...prevState,
          pendingPrintDesigns: [...d.slice(1)],
          completedPrintDesigns: [
            ...prevState.completedPrintDesigns,
            {
              ...d[0],
              success: isSuccess
            }
          ]
        }));
      };

      onSaveChanges(
        { productDesigns: [data[0]] },
        (updatedPrintDesign) => {
          setFormState((prev) => ({
            ...prev,
            selectedProductDesignId:
              prev.selectedProductDesignId === data[0].id
                ? updatedPrintDesign.id
                : prev.selectedProductDesignId,
            productDesigns: prev.productDesigns.map((t) =>
              t.id === data[0].id
                ? { ...t, ...updatedPrintDesign, hasChanges: false }
                : t
            )
          }));

          if (data[0].id === printTemplateState.id) {
            setPrintTemplateState((prev) => ({
              ...prev,
              ...updatedPrintDesign,
              hasChanges: false
            }));
          }

          onComplete(true, data);
          savePrintDesignsRecursively(
            [...data.slice(1)],
            [...uploadedDesigns, updatedPrintDesign]
          );
        },
        () => {
          onComplete(false, data);
          savePrintDesignsRecursively([...data.slice(1)], uploadedDesigns);
        }
      );
    }
    else {
      setFormState((prev) => ({
        ...prev,
        loading: false,
        showConfirmModal: false
      }));

      onSaveChangesComplete(uploadedDesigns);
    }
  };

  const saveDataUrlToStoragePromise = (dataUrl, fileName) =>
    new Promise((resolve, reject) => {
      dataUrlToFile(dataUrl, fileName, (file) => {
        if (file) {
          uploadFileToStorage(
            `ecom-vendor-${fkEcomVendor}-image-${fileName}`,
            file,
            (src) => {
              resolve(src);
            }
          );
        }
        else {
          reject(new Error('Failed to convert data URL to file')); // Reject if there's an issue
        }
      });
    });

  const getPrintDesignData = (
    variantDesign,
    externalProduct,
    defaultPrintTemplates,
    identifier
  ) =>
    Promise.all(
      variantDesign.designs.map(async (design, index) => {
        const canvasTemplate =
          defaultPrintTemplates.find(
            ({ fkEcomImagePosition }) =>
              String(fkEcomImagePosition) === String(design.fkEcomImagePosition)
          ) ?? defaultPrintTemplates[0];

        const {
          canvasState,
          imageUrl
        } = await createAndPopulateHiddenCanvasState(
          {
            templateImage: canvasTemplate.svgSource,
            canvasState: design.canvasState
          },
          true
        );

        return {
          ...design,
          designItems: canvasState.items
            .filter(({ visible }) => visible)
            .map((i) => ({
              ...i,
              itemProperties: i.itemPropertiesList
            })),
          canvasState,
          printImageUrl: await saveDataUrlToStoragePromise(
            imageUrl,
            `${identifier}-${index}`
          )
        };
      })
    );

  const getPrintVariantDesignsData = async (productDesign) => {
    const externalProduct = ecomExternalProducts.find(
      ({ id }) => id === productDesign.productId
    );

    const defaultPrintTemplates = getDefaultPrintTemplates(externalProduct);

    return Promise.all(
      productDesign.variantDesigns.map(async (variantDesign, index) => ({
        ...variantDesign,
        variantUuids: variantDesign.variantIds,
        printDesigns: await getPrintDesignData(
          variantDesign,
          externalProduct,
          defaultPrintTemplates,
          `${productDesign.id}-${variantDesign.id}-${index}`
        )
      }))
    );
  };

  const onSavePrintDesigns = (productDesignCollections = []) => {
    setSaveState((prevState) => ({
      ...prevState,
      allPrintDesigns: [...designsWithChanges],
      designs: [
        ...prevState.designs.map((d) => {
          const design = productDesignCollections.find(
            (i) => i.pkMedia === d.pkMedia
          );
          return design ? { ...d, pkDesign: design.pkDesign } : d;
        })
      ]
    }));

    Promise.all(
      designsWithChanges.map(async (productDesgin) => {
        const variantDesigns = await getPrintVariantDesignsData(productDesgin);

        const designCollection = productDesignCollections.find(
          (d) =>
            d.pkMedia ===
            (productDesgin.pkEcomVendorMedia || productDesgin.pkEcomStoreMedia)
        );

        return {
          id: productDesgin.id,
          pkProduct: productDesgin.pkProduct,
          pkPrintDesign: productDesgin.pkPrintDesign,
          pkDesign: designCollection?.pkDesign,
          productNameSuffix:
            saveState.appendDesignName && designCollection
              ? `${saveState.designNameSeperator?.trim()} ${
                  designCollection.name
                }`
              : null,
          isPrivate: saveState.isPrivate,
          externalProductId: productDesgin.productId,
          fkEcomExternalConnectionType:
            productDesgin.fkEcomExternalConnectionType,
          removedVariantUuids: productDesgin.removedVariantIds,
          printDesigns: variantDesigns.flatMap((v) =>
            v.printDesigns.map((d) => ({
              ...d,
              variantIds: v.variantIds
            }))
          )
        };
      })
    )
      .then((data) => {
        setSaveState((prevState) => ({
          ...prevState,
          allPrintDesigns: [...data],
          pendingPrintDesigns: [...data],
          completedPrintDesigns: []
        }));

        savePrintDesignsRecursively(data);
      })
      .catch((error) => {
        console.error('Error while processing templates:', error);
        triggerNotification();
        setFormState((prev) => ({ ...prev, loading: false }));
      });
  };

  const onSubmit = () => {
    hasBeenCancelled.current = false;
    setFormState((prev) => ({
      ...prev,
      loading: true
    }));

    const designsToSave = saveState.designs.filter(
      (d) => !d.deleted && !d.pkDesign && !!d.name
    );
    if (designsToSave.length > 0) {
      onCreateDesigns(
        { designs: designsToSave },
        (data) => {
          const productDesignCollections = saveState.designs
            .filter((d) => !d.deleted)
            .map((d) => ({
              ...d,
              pkDesign:
                d.pkDesign ||
                data.find((i) => i.pkMedia === d.pkMedia)?.pkDesign
            }));
          onSavePrintDesigns(productDesignCollections);
        },
        (error) => {
          setFormState((prev) => ({ ...prev, loading: false }));
          triggerNotification(error);
        }
      );
    }
    else {
      onSavePrintDesigns(saveState.designs.filter((d) => !d.deleted));
    }
  };

  const onCloseModal = () => {
    if (formState.loading) {
      setShowCancelConfirm(true);
    }
    else {
      setFormState((prev) => ({ ...prev, showConfirmModal: false }));
    }
  };

  return (
    <ResponsiveModal
      formSectionProps={
        formState.loading
          ? {
              isCancelHidden: true,
              submitColor: hasBeenCancelled.current ? 'dark' : 'red',
              submitTitle: hasBeenCancelled.current ? 'Close' : 'Cancel',
              onSubmit: () => {
                if (!hasBeenCancelled.current) {
                  setShowCancelConfirm(true);
                }
                else {
                  setFormState((prev) => ({
                    ...prev,
                    showConfirmModal: false
                  }));
                }
              }
            }
          : {
              onCancel: onCloseModal,
              onSubmit,
              submitColor: 'dark',
              submitTitle: 'Yes, save designs',
              cancelTitle: 'Cancel'
            }
      }
      onClose={onCloseModal}
      opened={formState.showConfirmModal}
      size={600}
      title={
        formState.loading
          ? `Saving ${singularPluralFormat(
              designsWithChanges.length,
              'design',
              'designs'
            )}`
          : `Save print ${singularPluralFormat(
              designsWithChanges.length,
              'design',
              'designs'
            )}?`
      }
    >
      {formState.loading ? (
        <AppStack style={{ gap: 20 }}>
          <AppStack style={{ flex: 1, gap: 5 }}>
            <Progress
              animated
              value={
                (Math.min(
                  saveState.completedPrintDesigns.length + 1,
                  saveState.allPrintDesigns.length
                ) /
                  saveState.allPrintDesigns.length) *
                100
              }
            />
            {hasBeenCancelled.current ? (
              <AppText
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  textAlign: 'center',
                  color: '#C40000'
                }}
              >
                Save Cancelled. Attempted (
                {Math.min(
                  saveState.completedPrintDesigns.length + 1,
                  saveState.allPrintDesigns.length
                )}
                /{saveState.allPrintDesigns.length})
              </AppText>
            ) : (
              <AppText
                style={{ fontSize: 14, fontWeight: 500, textAlign: 'center' }}
              >
                Saving print designs... (
                {Math.min(
                  saveState.completedPrintDesigns.length + 1,
                  saveState.allPrintDesigns.length
                )}
                /{saveState.allPrintDesigns.length})
              </AppText>
            )}
          </AppStack>
        </AppStack>
      ) : formState.showConfirm ? (
        <AppStack style={{ gap: 20 }}>
          <AppText style={{ fontSize: 14 }}>
            Are you sure you want to save{' '}
            <span style={{ fontWeight: 500 }}>
              {singularPluralFormat(
                designsWithChanges.length,
                'design',
                'designs'
              )}
            </span>
            ?
          </AppText>
        </AppStack>
      ) : (
        <AppStack style={{ gap: 20 }}>
          {designsWithoutChanges.length > 0 && (
            <AppCard shadow="none" style={{ padding: 0 }} withBorder>
              <AppFlexbox
                style={{
                  padding: 8,
                  gap: 8,
                  alignContent: 'center'
                }}
              >
                <AppStack>
                  <AlertCircle color="#eca70a" size={20} />
                </AppStack>
                <AppText style={{ fontSize: 14 }}>
                  {singularPluralFormat(
                    designsWithoutChanges.length,
                    'blank design',
                    'blank designs'
                  )}{' '}
                  without changes will be skipped.
                </AppText>
              </AppFlexbox>
            </AppCard>
          )}
          <AppStack style={{ gap: 16 }}>
            {designsWithChanges.length > 0 && (
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                {singularPluralFormat(
                  designsWithChanges.length,
                  'design',
                  'designs'
                )}{' '}
                with changes will be saved.
              </AppText>
            )}

            {newProducts.length > 0 && (
              <>
                <AppText
                  style={{ fontSize: 14, fontStyle: 'italic', color: '#666' }}
                >
                  {singularPluralFormat(
                    newProducts.length,
                    'new product',
                    'new products'
                  )}{' '}
                  will be created.
                </AppText>

                <Divider label="New product options" labelPosition="center" />

                <AppCheckbox
                  checked={saveState.isPrivate}
                  description="When selected, this product will be hidden from the public catalog but can still be added to any store."
                  label="Visible to SportsHeadz admins only"
                  onChange={() => {
                    setSaveState({
                      ...saveState,
                      isPrivate: !saveState.isPrivate
                    });
                  }}
                  styles={{ label: { fontWeight: 500 } }}
                />

                {saveState.designs.some((d) => !d.deleted) && (
                  <AppStack style={{ gap: 16, paddingBottom: 10 }}>
                    {newProducts.length >= 1 && (
                      <AppStack style={{ gap: 5 }}>
                        <AppCheckbox
                          checked={saveState.appendDesignName}
                          description="When selected, the design name will be added to the end of the product name using the seperator below."
                          label="Append design name to the end of the product name."
                          onChange={() => {
                            setSaveState({
                              ...saveState,
                              appendDesignName: !saveState.appendDesignName
                            });
                          }}
                          styles={{ label: { fontWeight: 500 } }}
                        />

                        {saveState.appendDesignName && (
                          <AppStack style={{ gap: 2 }}>
                            <TextInput
                              label="Seperator"
                              onChange={(e) => {
                                setSaveState({
                                  ...saveState,
                                  designNameSeperator: e.currentTarget.value
                                });
                              }}
                              style={{ flex: 1 }}
                              value={saveState.designNameSeperator}
                            />
                            <AppText style={{ fontSize: 14, color: '#999' }}>
                              {exampleNewProduct?.name || 'Product name'}
                              {` ${saveState.designNameSeperator?.trim()} ${
                                newProductDesigns[0]?.name || 'Design name'
                              }`}
                            </AppText>
                          </AppStack>
                        )}
                      </AppStack>
                    )}

                    <AppCheckbox
                      checked={saveState.addToDesigns}
                      description="If selected, products will be added to a design group with the same image selected. "
                      label="Add new products to design group"
                      onChange={() => {
                        setSaveState({
                          ...saveState,
                          addToDesigns: !saveState.addToDesigns
                        });
                      }}
                      styles={{ label: { fontWeight: 500 } }}
                    />

                    {saveState.addToDesigns && (
                      <AppStack style={{ gap: 5 }}>
                        <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                          Design groups:
                        </AppText>
                        <AppStack
                          style={{ gap: 0, border: 'solid 1px lightgrey' }}
                        >
                          {saveState.designs
                            .filter((d) => !d.deleted)
                            .map((design, index) => (
                              <React.Fragment key={design.pkMedia}>
                                {index > 0 && <Divider />}
                                <AppFlexbox
                                  style={{ gap: 0, alignItems: 'center ' }}
                                >
                                  <AppStack
                                    style={{
                                      padding: 8,
                                      width: 45,
                                      height: 45,
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <AppImage src={design.preview} />
                                  </AppStack>

                                  <Divider orientation="vertical" />
                                  <AppStack style={{ flex: 1, padding: 8 }}>
                                    <TextInput
                                      disabled={design.pkDesign}
                                      onChange={(e) => {
                                        setSaveState({
                                          ...saveState,
                                          designs: [
                                            ...saveState.designs.map((d) =>
                                              d.pkMedia === design.pkMedia
                                                ? {
                                                    ...d,
                                                    name: e.currentTarget.value
                                                  }
                                                : d
                                            )
                                          ]
                                        });
                                      }}
                                      placeholder="Design name"
                                      required
                                      style={{ flex: 1 }}
                                      value={design.name || ''}
                                    />
                                  </AppStack>
                                </AppFlexbox>
                              </React.Fragment>
                            ))}
                        </AppStack>
                      </AppStack>
                    )}
                  </AppStack>
                )}
              </>
            )}
          </AppStack>
        </AppStack>
      )}
      <ConfirmModal
        cancelActionText="Back"
        confirmActionColor="red"
        confirmActionText="Yes, cancel save"
        isOpen={showCancelConfirm}
        modalProps={{ size: 400 }}
        onCancel={() => {
          setShowCancelConfirm(false);
        }}
        onConfirm={() => {
          hasBeenCancelled.current = true;
          setShowCancelConfirm(false);
          setFormState((prev) => ({ ...prev, loading: false }));
        }}
        title="Cancel saving print designs?"
      >
        <AppText style={{ fontSize: 14 }}>
          Are you sure you want to cancel saving your designs?
        </AppText>
      </ConfirmModal>
    </ResponsiveModal>
  );
};

PrintDesignEditorSaveChangesModal.propTypes = {
  canvasController: PropTypes.object.isRequired,
  ecomExternalProducts: PropTypes.array.isRequired,
  formState: PropTypes.object.isRequired,
  onSaveChanges: PropTypes.func.isRequired,
  printTemplateState: PropTypes.object.isRequired,
  setFormState: PropTypes.func.isRequired,
  fkEcomStore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fkEcomVendor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCreateDesigns: PropTypes.func,
  onSaveChangesComplete: PropTypes.func,
  options: PropTypes.object,
  setPrintTemplateState: PropTypes.func
};

export default PrintDesignEditorSaveChangesModal;
